import {
  Component,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { EmptyStateComponent } from '@fc-shared/ui/empty-state/empty-state.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ScrollNearEndDirective } from '@fc-shared/directives/scroll-near-end.directive';
import { SkeletonModule } from '@fc-shared/ui/skeleton/skeleton.module';
import { NotificationsStore } from '@fc-core/notifications/store/notifications.store';
import { NotificationsFacadeService } from '@fc-core/notifications/store/notifications-facade.service';
import { EventItemComponent } from '@fc-core/notifications/events-tab/event-item.component';
import moment from 'moment-timezone';
import { DATE_TIME_FORMATS } from '@fc-shared/utils/date-time-formats';

@Component({
  selector: 'fc-event-list',
  imports: [
    EmptyStateComponent,
    MatProgressSpinner,
    ScrollNearEndDirective,
    SkeletonModule,
    EventItemComponent,
  ],
  template: ` @if (loading()) {
      <div class="h-full">
        <fc-skeleton-list></fc-skeleton-list>
      </div>
    } @else {
      <div
        fcScrollNearEnd
        (nearEnd)="notificationsStore.loadMoreEvents()"
        class="flex h-full flex-col overflow-y-auto"
      >
        @for (event of notifications(); track i; let i = $index) {
          @if (
            i === 0 ||
            !isSameEventDay(
              $safeNavigationMigration(event?.created),
              $safeNavigationMigration(notifications()[i - 1]?.created)
            )
          ) {
            <div
              class="p-16 text-center text-sm text-grey-500 border-b border-grey-100"
            >
              <span>{{
                formatMessageDateDivider(
                  $safeNavigationMigration(event?.created)
                )
              }}</span>
            </div>
          }
          <fc-event-item
            [event]="event"
            (markAsRead)="notificationsStore.markAsRead($event)"
          />
        } @empty {
          <fc-empty-state
            class="m-auto"
            icon="icon-notification-outline"
            subtitle="You have no notifications yet"
          ></fc-empty-state>
        }
      </div>
    }

    @if (notificationsStore.selectors.loadingMore()) {
      <div class="events-list-load-more">
        <mat-spinner
          class="spinner"
          diameter="30"
          strokeWidth="4"
        ></mat-spinner>
      </div>
    }`,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        display: block;
        height: 100%;
        overflow-y: hidden;
      }

      .events-list-load-more {
        width: 100%;
        padding: 16px 0;
        display: flex;
        justify-content: center;
      }
    `,
  ],
})
export class EventListComponent {
  selectedTab = input<string>();
  notificationsStore = inject(NotificationsStore);
  notificationsFacade = inject(NotificationsFacadeService);
  notifications = this.notificationsFacade.getNotifications();
  loading = this.notificationsStore.selectors.loading;

  formatMessageDateDivider(date: string): string {
    if (moment(date).isSame(moment(), 'day')) {
      return 'Today';
    }
    return moment(date).format(DATE_TIME_FORMATS.displayDate);
  }

  isSameEventDay(currentDate: string, previousDate: string): boolean {
    return moment(currentDate).isSame(moment(previousDate), 'day');
  }
}
